/**
 * Starting points for a new eval — the "simple eval" the common case never
 * has to be harder than a flat eval was (TRO-425).
 *
 * Each template is a complete, valid graph in the TRO-412 model: nodes that
 * declare typed output fields, conditions on the edges, a required default,
 * and verdict nodes at the leaves. The builder edits these; it never starts
 * from an empty canvas. `graph(target)` adapts the default Python / prompt
 * to what the eval scores.
 */
import { DEFAULT_JUDGE_PROMPT } from 'features/evals3/editor/nodeKinds';

const SUBJECT = { run: 'run', span: 'span', conversation: 'conversation' };
const OUTPUT_EXPR = { run: 't.run.output', span: 't.span.output', conversation: 't.conversation.transcript' };
const PROMPT_FOR = {
  run: DEFAULT_JUDGE_PROMPT,
  span: DEFAULT_JUDGE_PROMPT.replace(/run\.input/g, 'span.input').replace(/run\.output/g, 'span.output').replace('one answer from an AI assistant', 'one step of an AI agent'),
  conversation: [
    'You are grading a whole conversation between a user and an AI assistant.',
    '',
    'Transcript:',
    '{{conversation.transcript}}',
    '',
    'Score how well the assistant served the user across the conversation, from 0 (poor) to 1 (excellent).',
  ].join('\n'),
};

const pythonCheck = (target) => ({
  type: 'guard',
  name: 'Check',
  instrument: 'python',
  config: {
    source: [
      'def evaluate(t):',
      `    # t is the trace: t.${SUBJECT[target]}, t.meta${target === 'run' ? ', t.spans' : ''}`,
      `    # return True when this ${SUBJECT[target]} passes the check`,
      `    return 'error' not in text(${OUTPUT_EXPR[target]}).lower()`,
    ].join('\n'),
  },
  output: { ok: { type: 'boolean' } },
  edges: [{ when: { field: 'ok', op: 'eq', value: true }, to: 'pass' }, { default: 'fail' }],
});

const judge = (target, { to_pass = 'pass', to_fail = 'fail' } = {}) => ({
  type: 'judgment',
  name: 'Judge',
  instrument: 'llm_judge',
  config: { prompt: { user: PROMPT_FOR[target] }, model_params: { temperature: 0 } },
  output: { score: { type: 'number', min: 0, max: 1 } },
  edges: [{ when: { field: 'score', op: 'gte', value: 0.7 }, to: to_pass }, { default: to_fail }],
});

const pass = (name = 'Pass') => ({ type: 'verdict', name, config: { passed: true } });
const fail = (reason, name = 'Fail') => ({ type: 'verdict', name, config: { passed: false, reason_code: reason } });

export const TEMPLATES = [
  {
    id: 'python',
    label: 'Python check',
    blurb: 'Hand-written evaluate(t) returns pass or fail. Runs on every event, no model call.',
    kinds: ['python'],
    hasJudge: false,
    graph: (target = 'run') => ({
      schema_version: 2, entry: 'check',
      nodes: { check: pythonCheck(target), pass: pass(), fail: fail('CHECK_FAILED') },
    }),
  },
  {
    id: 'judge',
    label: 'LLM judge',
    blurb: 'A model scores the event against your prompt; a threshold on the score decides.',
    kinds: ['llm_judge'],
    hasJudge: true,
    graph: (target = 'run') => ({
      schema_version: 2, entry: 'judge',
      nodes: { judge: judge(target), pass: pass(), fail: fail('LOW_SCORE') },
    }),
  },
  {
    id: 'screen_then_judge',
    label: 'Screen, then judge',
    blurb: 'A cheap Python screen decides the obvious cases; only what it cannot decide reaches the model.',
    kinds: ['python', 'llm_judge'],
    hasJudge: true,
    graph: (target = 'run') => ({
      schema_version: 2, entry: 'screen',
      nodes: {
        screen: {
          type: 'dispatch', name: 'Screen', instrument: 'python',
          config: {
            source: [
              'def evaluate(t):',
              `    out = text(${OUTPUT_EXPR[target]}).lower()`,
              '    if not out.strip():',
              "        return 'fail'      # empty answer: no judge needed",
              "    if 'i cannot help' in out or 'i can\\'t help' in out:",
              "        return 'fail'",
              "    return 'unsure'        # let the judge decide",
            ].join('\n'),
          },
          output: { route: { type: 'enum', values: ['pass', 'fail', 'unsure'] } },
          edges: [
            { when: { field: 'route', op: 'eq', value: 'pass' }, to: 'pass' },
            { when: { field: 'route', op: 'eq', value: 'fail' }, to: 'fail_screen' },
            { default: 'judge' },
          ],
        },
        judge: judge(target, { to_pass: 'pass_judge', to_fail: 'fail_judge' }),
        pass: pass('Pass (screen)'),
        pass_judge: pass('Pass (judge)'),
        fail_screen: fail('SCREEN', 'Fail (screen)'),
        fail_judge: fail('JUDGE', 'Fail (judge)'),
      },
    }),
  },
  {
    id: 'human',
    label: 'Screen, then a person',
    blurb: 'A Python screen passes the clear cases; everything else waits in the grading queue for a person.',
    kinds: ['python', 'human'],
    hasJudge: false,
    graph: (target = 'run') => ({
      schema_version: 2, entry: 'screen',
      nodes: {
        screen: {
          ...pythonCheck(target), name: 'Screen',
          edges: [{ when: { field: 'ok', op: 'eq', value: false }, to: 'fail_screen' }, { default: 'review' }],
        },
        review: {
          type: 'human', name: 'Review', instrument: 'human',
          config: { queue_config: { instructions: 'Is this answer acceptable? Pick yes or no.' } },
          output: { choice: { type: 'enum', values: ['yes', 'no'] } },
          edges: [{ when: { field: 'choice', op: 'eq', value: 'yes' }, to: 'pass' }, { default: 'fail_review' }],
        },
        pass: pass(),
        fail_screen: fail('SCREEN', 'Fail (screen)'),
        fail_review: fail('REVIEW', 'Fail (review)'),
      },
    }),
  },
];

export const TARGETS = [
  { value: 'run', label: 'Run', plural: 'runs' },
  { value: 'span', label: 'Span', plural: 'spans' },
  { value: 'conversation', label: 'Conversation', plural: 'conversations' },
];

export const RATINGS = [
  { value: 'binary', label: 'Pass / fail', blurb: 'The verdict alone. Clear, and enough for most checks.', needsJudge: false },
  { value: 'score', label: 'Pass / fail with a score', blurb: 'The judge\'s 0–1 score is shown beside the verdict, and a threshold on it decides.', needsJudge: true },
];

/**
 * Apply the chosen rating to a template graph: with `score`, every judge
 * threshold becomes `threshold`, and the verdicts the judge leads to show
 * the judge's score.
 */
export function applyRating(graph, { rating, threshold }) {
  const g = JSON.parse(JSON.stringify(graph));
  const judges = Object.entries(g.nodes).filter(([, n]) => n.instrument === 'llm_judge');
  for (const [id, n] of judges) {
    for (const e of n.edges || []) {
      if (e.when && e.when.field === 'score' && rating === 'score' && Number.isFinite(threshold)) e.when.value = threshold;
      const to = e.default !== undefined ? e.default : e.to;
      const v = g.nodes[to];
      if (v && v.type === 'verdict') {
        if (rating === 'score') v.config = { ...v.config, score_from: { node_id: id, field: 'score' } };
        else if (v.config) delete v.config.score_from;
      }
    }
  }
  return g;
}
